/**
 * ContactEmailSection
 *
 * Toont alle email communicatie met een contact over alle modules heen.
 * Filterbaar op module (Alle, Helpdesk, Incasso, Andere).
 * Klik op email → detail + antwoord-knop die de juiste thread hervat.
 *
 * Endpoint: GET /contact/:id/emails
 * Reply:    POST /helpdesk/ticket/:id/send-email  (helpdesk)
 *           POST /email/incasso-ticket/:id/send   (incasso)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import {
  Envelope,
  ArrowRight,
  ArrowLeft,
  X,
  PaperPlaneTilt,
  Paperclip,
} from '@phosphor-icons/react';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

// ─── Types ────────────────────────────────────────────────────────────────────

type EmailEntityType =
  | 'helpdesk'
  | 'helpdesk_supplier'
  | 'incasso'
  | 'acquisition'
  | 'insurance'
  | 'renovation_project'
  | 'renovation_part'
  | string;

interface ContactEmail {
  id: number;
  messageId: string;
  subject: string;
  body: string;
  from: string;
  receivedDateTime: string;
  direction: 'sent' | 'received';
  isRead: boolean;
  hasAttachments?: boolean;
  entityType: EmailEntityType;
  entityId: number;
  threadId?: string;
  metadata?: { to?: string };
}

type FilterType = 'all' | 'helpdesk' | 'incasso' | 'other';

interface ContactEmailSectionProps {
  contactId: number;
  contactEmail?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MODULE_LABELS: Record<string, string> = {
  helpdesk: 'Helpdesk',
  helpdesk_supplier: 'Helpdesk',
  incasso: 'Incasso',
  acquisition: 'Acquisitie',
  insurance: 'Verzekering',
  renovation_project: 'Renovatie',
  renovation_part: 'Renovatie',
};

const MODULE_COLORS: Record<string, string> = {
  helpdesk: 'bg-blue-50 text-blue-700 border-blue-200',
  helpdesk_supplier: 'bg-blue-50 text-blue-700 border-blue-200',
  incasso: 'bg-red-50 text-red-700 border-red-200',
  acquisition: 'bg-purple-50 text-purple-700 border-purple-200',
  insurance: 'bg-amber-50 text-amber-700 border-amber-200',
  renovation_project: 'bg-green-50 text-green-700 border-green-200',
  renovation_part: 'bg-green-50 text-green-700 border-green-200',
};

const FILTER_TABS: { key: FilterType; label: string }[] = [
  { key: 'all', label: 'Alle' },
  { key: 'helpdesk', label: 'Helpdesk' },
  { key: 'incasso', label: 'Incasso' },
  { key: 'other', label: 'Andere' },
];

const isOtherType = (type: string) =>
  !['helpdesk', 'helpdesk_supplier', 'incasso'].includes(type);

// ─── Component ────────────────────────────────────────────────────────────────

export const ContactEmailSection: React.FC<ContactEmailSectionProps> = ({
  contactId,
  contactEmail,
}) => {
  const { session } = useSession();

  const [emails, setEmails] = useState<ContactEmail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedEmail, setSelectedEmail] = useState<ContactEmail | null>(null);

  // Reply modal
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTo, setReplyTo] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [isSending, setIsSending] = useState(false);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchEmails = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/contact/${contactId}/emails`,
        { method: 'GET' },
        session,
      );
      if (!res.ok) throw new Error('Fout bij ophalen emails');
      const data: ContactEmail[] = await res.json();
      setEmails(data);
    } catch (err) {
      console.error('[ContactEmailSection]', err);
      toast.error('Fout bij laden van emails');
    } finally {
      setIsLoading(false);
    }
  }, [contactId, session]);

  useEffect(() => {
    if (contactId) fetchEmails();
  }, [fetchEmails]);

  // ─── Filter ─────────────────────────────────────────────────────────────────

  const filtered = emails.filter((e) => {
    if (filter === 'all') return true;
    if (filter === 'helpdesk') return e.entityType === 'helpdesk' || e.entityType === 'helpdesk_supplier';
    if (filter === 'incasso') return e.entityType === 'incasso';
    if (filter === 'other') return isOtherType(e.entityType);
    return true;
  });

  // ─── Open reply modal ────────────────────────────────────────────────────────

  const openReply = (email: ContactEmail) => {
    const threadRef = email.threadId ?? '';
    const subjectPrefix = email.subject.startsWith('Re:') ? '' : 'Re: ';
    const subjectBase = email.subject.replace(/\s*\[.*?\]\s*$/, '').trim();

    setReplyTo(email.from !== contactEmail ? email.from : (contactEmail ?? ''));
    setReplySubject(`${subjectPrefix}${subjectBase} [${threadRef}]`);
    setReplyContent('');
    setSelectedEmail(email);
    setReplyOpen(true);
  };

  // ─── Send reply ──────────────────────────────────────────────────────────────

  const handleSendReply = async () => {
    if (!selectedEmail || !replyTo.trim() || !replyContent.trim()) {
      toast.error('Vul alle velden in');
      return;
    }

    setIsSending(true);
    try {
      let url: string;
      let body: object;

      if (selectedEmail.entityType === 'helpdesk' || selectedEmail.entityType === 'helpdesk_supplier') {
        url = `${API_URL}/helpdesk/ticket/${selectedEmail.entityId}/send-email`;
        body = {
          subject: replySubject,
          content: replyContent,
          replyToMessageId: selectedEmail.messageId,
        };
      } else if (selectedEmail.entityType === 'incasso') {
        url = `${API_URL}/email/incasso-ticket/${selectedEmail.entityId}/send`;
        body = {
          subject: replySubject,
          content: replyContent,
          recipientEmail: replyTo,
          replyToMessageId: selectedEmail.messageId,
        };
      } else {
        toast.error('Antwoorden is niet beschikbaar voor dit type email');
        return;
      }

      const res = await fetchWithAuth(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        session,
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Verzenden mislukt');
      }

      toast.success('Antwoord verzonden');
      setReplyOpen(false);
      fetchEmails();
    } catch (err: any) {
      console.error('[ContactEmailSection] send reply error:', err);
      toast.error(err.message || 'Fout bij verzenden');
    } finally {
      setIsSending(false);
    }
  };

  // ─── Counts per filter ──────────────────────────────────────────────────────

  const counts: Record<FilterType, number> = {
    all: emails.length,
    helpdesk: emails.filter((e) => e.entityType === 'helpdesk' || e.entityType === 'helpdesk_supplier').length,
    incasso: emails.filter((e) => e.entityType === 'incasso').length,
    other: emails.filter((e) => isOtherType(e.entityType)).length,
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Envelope size={18} className="text-moss-primary" />
        <h3 className="font-semibold text-neutral-700">E-mail communicatie</h3>
        <span className="text-xs text-neutral-400">({emails.length})</span>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-neutral-200">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${
              filter === tab.key
                ? 'bg-white border border-b-white border-neutral-200 -mb-px text-moss-primary'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {tab.label}
            {counts[tab.key] > 0 && (
              <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${
                filter === tab.key ? 'bg-moss-primary/10 text-moss-primary' : 'bg-neutral-100'
              }`}>
                {counts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="py-8 text-center text-sm text-neutral-secondary-mid">
          Emails laden...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-center">
          <Envelope size={36} className="text-neutral-300 mb-3" />
          <p className="text-sm font-medium text-neutral-700">Geen emails gevonden</p>
          <p className="text-xs text-neutral-secondary-mid mt-1">
            {filter === 'all'
              ? 'Er is nog geen email communicatie met dit contact.'
              : `Geen ${FILTER_TABS.find((t) => t.key === filter)?.label.toLowerCase()} emails gevonden.`}
          </p>
        </div>
      ) : (
        <>
          {/* Email list */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 border-b">
                <tr>
                  <th className="text-left p-2 font-medium text-neutral-600 w-20">Richting</th>
                  <th className="text-left p-2 font-medium text-neutral-600 w-32">Datum</th>
                  <th className="text-left p-2 font-medium text-neutral-600 w-24">Module</th>
                  <th className="text-left p-2 font-medium text-neutral-600">Onderwerp</th>
                  <th className="text-left p-2 font-medium text-neutral-600 w-20">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((email) => {
                  const isSent = email.direction === 'sent';
                  const isSelected = selectedEmail?.id === email.id;
                  const moduleLabel = MODULE_LABELS[email.entityType] ?? email.entityType;
                  const moduleColor = MODULE_COLORS[email.entityType] ?? 'bg-neutral-100 text-neutral-600 border-neutral-200';

                  return (
                    <tr
                      key={email.id}
                      className={`cursor-pointer hover:bg-neutral-50 transition-colors ${
                        isSelected ? 'bg-moss-primary/5 border-l-2 border-moss-primary' : ''
                      } ${!isSent && !email.isRead ? 'font-semibold' : ''}`}
                      onClick={() => setSelectedEmail(isSelected ? null : email)}
                    >
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          {isSent ? (
                            <ArrowRight size={12} className="text-moss-primary shrink-0" />
                          ) : (
                            <ArrowLeft size={12} className="text-blue-600 shrink-0" />
                          )}
                          <span className={`text-[10px] ${isSent ? 'text-moss-primary' : 'text-blue-600'}`}>
                            {isSent ? 'Verzonden' : 'Ontvangen'}
                          </span>
                        </div>
                      </td>
                      <td className="p-2 text-neutral-500">
                        {new Date(email.receivedDateTime).toLocaleDateString('nl-BE', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                        })}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className={`text-[10px] ${moduleColor}`}>
                          {moduleLabel}
                          {email.entityId ? ` #${email.entityId}` : ''}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <span className="truncate max-w-xs block">
                          {email.subject}
                          {email.hasAttachments && (
                            <Paperclip size={10} className="inline ml-1 text-neutral-400" />
                          )}
                        </span>
                      </td>
                      <td className="p-2">
                        {!isSent && !email.isRead && (
                          <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                            Ongelezen
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Selected email detail */}
          {selectedEmail && (
            <div className="border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-neutral-50 border-b">
                <div className="flex items-center gap-2">
                  <Envelope size={16} className="text-moss-primary" />
                  <span className="text-sm font-semibold text-neutral-700">
                    {MODULE_LABELS[selectedEmail.entityType] ?? selectedEmail.entityType}
                    {selectedEmail.entityId ? ` — #${selectedEmail.entityId}` : ''}
                  </span>
                </div>
                <button onClick={() => setSelectedEmail(null)} className="text-neutral-400 hover:text-neutral-700">
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="text-xs text-neutral-600 space-y-0.5">
                  <div><strong>Onderwerp:</strong> {selectedEmail.subject}</div>
                  <div><strong>Van:</strong> {selectedEmail.from}</div>
                  {selectedEmail.metadata?.to && (
                    <div><strong>Naar:</strong> {selectedEmail.metadata.to}</div>
                  )}
                  <div>
                    <strong>Datum:</strong>{' '}
                    {new Date(selectedEmail.receivedDateTime).toLocaleString('nl-BE')}
                  </div>
                </div>

                <div
                  className="prose prose-sm max-w-none bg-white border rounded p-3 text-xs max-h-60 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
                />

                {(selectedEmail.entityType === 'helpdesk' ||
                  selectedEmail.entityType === 'helpdesk_supplier' ||
                  selectedEmail.entityType === 'incasso') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openReply(selectedEmail)}
                  >
                    <ArrowLeft size={14} className="mr-1.5" />
                    Antwoorden
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Reply modal */}
      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Antwoorden via{' '}
              {selectedEmail?.entityType === 'incasso' ? 'incasso@yally.be' : 'helpdesk@yally.be'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="reply-to">Aan</Label>
              <Input
                id="reply-to"
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reply-subject">Onderwerp</Label>
              <Input
                id="reply-subject"
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reply-content">Bericht</Label>
              <Textarea
                id="reply-content"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Typ uw antwoord..."
                className="min-h-[120px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyOpen(false)} disabled={isSending}>
              Annuleren
            </Button>
            <Button
              onClick={handleSendReply}
              disabled={isSending}
              className="bg-moss-primary text-white hover:bg-moss-primary/90"
            >
              <PaperPlaneTilt size={14} className="mr-1.5" />
              {isSending ? 'Verzenden...' : 'Verstuur antwoord'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
